<template>
  <div class="h3">Питание</div>

  <div v-if="!isQuestionnaireSent" class="paragraph-s_h text-secondary mt-4">
    <span class="text-red">*</span> — поля обязательные для заполнения
  </div>

  <e-form v-if="!isQuestionnaireSent" ref="formRef" class="mt-10">
    <inputs-group title="Объекты питания">
      <div class="w-100">
        <expand-card
          v-for="(restaurant, index) in form.restaurants"
          :key="index"
          ref="expandCardRefs"
          :expanded="restaurant.expanded"
          :error="cardsWithError.includes(index)"
          title="Объект питания"
          class="mb-4"
        >
          <template #title-append>
            <published-entity-icon v-if="restaurant.questionnaireId !== questionnaire?.id" />
            <e-btn icon variant="ghost" size="S" @click.stop="removeRestaurant(index)">
              <e-icon name="trash" />
            </e-btn>
          </template>

          <e-input v-model="restaurant.name" class="mb-4" :rules="[required(), maxLength(100)]">
            <template #label>
              <div>Название<span class="text-red">*</span></div>
            </template>
          </e-input>

          <e-select
            v-model="restaurant.type"
            label="Тип"
            :items="dictionaries?.nutrition.foodObjectType || []"
            class="mb-4"
          />

          <inputs-group title="Кухня" class="mt-10">
            <combobox-with-list
              v-model="restaurant.cuisine"
              label="Добавить"
              :options="dictionaries?.nutrition.cuisine || []"
            />
          </inputs-group>

          <inputs-group title="Время работы" class="mt-10">
            <e-time-picker v-model="restaurant.openingHoursFrom" class="inputs-group__tiny-input">
              <template #label> С </template>
            </e-time-picker>
            <e-time-picker v-model="restaurant.openingHoursTo" class="inputs-group__tiny-input">
              <template #label> До </template>
            </e-time-picker>
          </inputs-group>

          <inputs-group title="Фотографии объектa питания" class="mt-10">
            <template #subtitle>
              Загрузите фотографии или укажите ссылку на фотобанк
              <div class="mt-1">Если вы уже загрузили фотографии, нет необходимости их дублировать.</div>
            </template>

            <content-images-uploader
              :ref="addUploaderRef"
              v-model="restaurant.images"
              v-model:images-link="restaurant.photobankUrl"
              v-model:attachments="restaurant.attachments"
              :content-section="ContentSection.FoodObjects"
              :section-object-name="restaurant.id"
              @uploaded="saveIfHasChanges"
            />
          </inputs-group>
        </expand-card>
      </div>
      <e-btn variant="link" prepend-icon-src="plus" @click="addRestaurant">Добавить объект питания</e-btn>
    </inputs-group>

    <inputs-group title="Питание детей" class="mt-10">
      <combobox-with-list
        v-model="form.childrenNutrition"
        label="Добавить"
        :options="dictionaries?.nutrition.childrenNutrition || []"
      />
    </inputs-group>

    <inputs-group title="Доставка еды в номер" class="mt-10">
      <e-radio-group v-model="form.nutritionDelivery" :items="deliveryOptions" inline />
    </inputs-group>

    <div class="d-flex justify-space-between mt-10">
      <e-btn variant="secondary" prepend-icon-src="arrowLeft" @click="back">Назад</e-btn>
      <e-btn variant="primary" append-icon-src="arrowRight" :loading="isLoading" @click="onSubmit(true)">Далее</e-btn>
    </div>
  </e-form>

  <div v-else class="mt-10">
    <template v-if="form.restaurants.length">
      <div class="paragraph-m_h font-600 mt-10 mb-4">Объекты питания</div>
      <expand-card
        v-for="(restaurant, index) in form.restaurants"
        :key="index"
        title="Объект питания"
        expanded
        class="mb-4"
      >
        <div class="d-flex flex-column row-gap-10">
          <property-item title="Наименование">
            {{ restaurant.name }}
          </property-item>

          <div v-if="restaurant.type">
            <property-item title="Тип">
              {{ restaurant.type }}
            </property-item>
          </div>

          <div v-if="restaurant.cuisine.length">
            <div class="paragraph-m_h font-600 mb-2">Кухня</div>
            <e-chip v-for="cuisine in restaurant.cuisine" :key="cuisine" class="mt-2 mr-2">
              {{ cuisine }}
            </e-chip>
          </div>

          <div v-if="restaurant.openingHoursFrom || restaurant.openingHoursTo">
            <div class="paragraph-m_h font-600 mb-2">Время работы</div>

            <div class="d-flex">
              <property-item v-if="restaurant.openingHoursFrom" title="С">
                {{ getTimeFromPTH(restaurant.openingHoursFrom) }}
              </property-item>
              <property-item v-if="restaurant.openingHoursTo" title="До" class="ml-10">
                {{ getTimeFromPTH(restaurant.openingHoursTo) }}
              </property-item>
            </div>
          </div>
        </div>

        <div v-if="restaurant.images.length || restaurant.photobankUrl" class="paragraph-m_h font-600 mt-10 mb-2">
          Фотографии
        </div>

        <images-gallery v-if="restaurant.images.length" class="mt-4" :files="restaurant.images" />

        <property-item v-if="restaurant.photobankUrl" title="Ссылка на фотобанк" class="mt-4">
          <a :href="restaurant.photobankUrl" target="_blank" class="link">{{ restaurant.photobankUrl }}</a>
        </property-item>
      </expand-card>
    </template>

    <template v-if="form.childrenNutrition.length">
      <div class="paragraph-m_h font-600 mb-2">Питание детей</div>
      <e-chip v-for="childrenNutrition in form.childrenNutrition" :key="childrenNutrition" class="mt-2 mr-2">
        {{ childrenNutrition }}
      </e-chip>
    </template>

    <template v-if="form.nutritionDelivery != null">
      <div class="paragraph-m_h font-600 mb-2 mt-10">Доставка еды в номер</div>
      <e-radio-group v-model="form.nutritionDelivery" inline disabled :items="deliveryOptions" />
    </template>

    <div class="d-flex justify-space-between mt-10">
      <e-btn variant="secondary" prepend-icon-src="arrowLeft" @click="back">Назад</e-btn>
      <e-btn variant="primary" append-icon-src="arrowRight" :loading="isLoading" @click="nextStep">Далее</e-btn>
    </div>
  </div>

  <remove-entity-confirmation v-model="isRemoveEntityConfirmationOpen" :action="_removeRestaurant" />
</template>

<script setup lang="ts">
import { type ComponentPublicInstance, onBeforeUnmount, onMounted, reactive, ref, toRef, watch } from 'vue';
import RemoveEntityConfirmation from './RemoveEntityConfirmation.vue';
import type { EFormType } from '@/core/types/components/components';
import type { LoadedFile } from '@/core/types/files';
import ComboboxWithList from '@/core/components/interface/ComboboxWithList.vue';
import ExpandCard from '@/core/components/interface/ExpandCard.vue';
import InputsGroup from '@/core/components/interface/InputsGroup.vue';
import PropertyItem from '@/core/components/interface/PropertyItem.vue';
import { EBtn, EChip, EForm, EIcon, EInput, ERadioGroup, ESelect, ETimePicker } from '@/core/components/ui';
import ImagesGallery from '@/core/components/ui/files/filesGallery.vue';
import { elementErrorSelector } from '@/core/consts/components';
import { getTimeFromPTH } from '@/core/utils/date';
import { compareObjects } from '@/core/utils/objects/get-delta';
import { sleep } from '@/core/utils/sleep';
import { maxLength, required } from '@/core/utils/validation';
import { QuestionnaireMasterStep } from '@/graphql/generated';
import ContentImagesUploader from '@/parts/content/components/ContentImagesUploader.vue';
import PublishedEntityIcon from '@/parts/content/components/PublishedEntityIcon.vue';
import { useContentStore } from '@/parts/content/store';
import { ContentSection } from '@/parts/content/types';
import { useContentImagesUpload } from '@/parts/content/utils';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';

const { accommodationObject } = useAccommodationObjectsStore();

const {
  dictionaries,
  back,
  saveQuestionnaire,
  saveQuestionnaireDebounced,
  moveToNextStep,
  questionnaireAnswers,
  questionnaire,
  isQuestionnaireSent,
  nextStep,
  needValidate,
  completeValidation,
} = useContentStore();
const { initImages, deleteImage } = useContentImagesUpload(toRef(() => accommodationObject.value?.cid));

const formRef = ref<EFormType>();

const expandCardRefs = ref<ComponentPublicInstance[]>([]);
const cardsWithError = ref<number[]>([]);

const uploaderRefs = ref<any[]>([]);

const form = reactive({
  restaurants:
    questionnaireAnswers.value?.nutritionInfo?.foodObjects?.map((foodObject) => ({
      expanded: false,
      id: foodObject.foodObjectId,
      name: foodObject.foodObjectName,
      type: foodObject.foodObjectType,
      cuisine: foodObject.cuisine || [],
      openingHoursFrom: foodObject.foodObjectOpeningHoursFrom,
      openingHoursTo: foodObject.foodObjectOpeningHoursTo,
      attachments: foodObject.attachments ?? [],
      photobankUrl: foodObject.photobankUrl ?? null,
      images: [] as LoadedFile[],
      questionnaireId: foodObject.questionnaireId,
    })) || [],
  childrenNutrition: questionnaireAnswers.value?.nutritionInfo?.childrenNutrition || [],
  nutritionDelivery: questionnaireAnswers.value?.nutritionInfo?.nutritionDelivery,
});

const save = () => saveQuestionnaireDebounced(mapAnswersToSave());
const saveIfHasChanges = async () => {
  if (hasChanges()) {
    await saveQuestionnaire(mapAnswersToSave());
  }
};

watch(
  form,
  () => {
    void save();
  },
  {
    deep: true,
  },
);

form.restaurants.forEach((restaurant, restaurantIndex: number) => {
  void initImages(
    restaurant.attachments,
    restaurant.images,
    ContentSection.FoodObjects,
    form.restaurants[restaurantIndex].id,
  );
});

const addUploaderRef = (el: any) => {
  if (el && !uploaderRefs.value.includes(el)) {
    uploaderRefs.value.push(el);
  }
};

const addRestaurant = () => {
  form.restaurants.push({
    expanded: true,
    id: crypto.randomUUID(),
    name: '',
    type: null,
    cuisine: [],
    openingHoursFrom: null,
    openingHoursTo: null,
    attachments: [],
    photobankUrl: null,
    images: [] as LoadedFile[],
    questionnaireId: questionnaire.value?.id,
  });
};

const isRemoveEntityConfirmationOpen = ref(false);
const removingRestaurant = ref<number>();

const _removeRestaurant = async () => {
  if (removingRestaurant.value === undefined) {
    return;
  }

  const removedIndex = removingRestaurant.value;
  const restaurantToRemove = form.restaurants.splice(removedIndex, 1)[0];
  
  for (const file of restaurantToRemove.images) {
    await deleteImage(file.id, ContentSection.FoodObjects, restaurantToRemove.id, restaurantToRemove.attachments);
  }

  uploaderRefs.value = [];

  removingRestaurant.value = undefined;
};

const removeRestaurant = async (index: number) => {
  removingRestaurant.value = index;

  const restaurant = form.restaurants[index];

  if (restaurant?.questionnaireId !== questionnaire.value?.id) {
    isRemoveEntityConfirmationOpen.value = true;
  } else {
    void _removeRestaurant();
  }
};

const isLoading = ref(false);

const validate = async () => {
  await sleep(0);
  cardsWithError.value = [];

  const isFormValid = await formRef.value?.validate();

  expandCardRefs.value.forEach((card, index) => {
    const error = card.$el.querySelector(elementErrorSelector);
    if (error && !cardsWithError.value.includes(index)) {
      cardsWithError.value.push(index);
    }
  });

  if (!isFormValid || cardsWithError.value.length > 0) {
    if (cardsWithError.value.length > 0) {
      const firstErrorIndex = cardsWithError.value[0];
      expandCardRefs.value[firstErrorIndex]?.$el?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
    }
    return false;
  }

  for (const uploaderRef of uploaderRefs.value) {
    if (uploaderRef && !uploaderRef.validate()) {
      return false;
    }
  }

  return true;
};

const hasChanges = () => {
  if (questionnaireAnswers.value?.nutritionInfo) {
    delete questionnaireAnswers.value.nutritionInfo.__typename;
    questionnaireAnswers.value.nutritionInfo.foodObjects?.forEach((foodObject) => {
      delete foodObject.__typename;
    });

    if (!compareObjects(mapAnswersToSave().nutritionInfo, questionnaireAnswers.value.nutritionInfo)) {
      return true;
    }
  }

  return false;
};

const onSubmit = async (force = false) => {
  if (!force && !hasChanges()) {
    return true;
  }

  cardsWithError.value = [];

  const isValid = await validate();
  if (!isValid) {
    return false;
  }

  isLoading.value = true;
  void moveToNextStep(QuestionnaireMasterStep.NutritionInfo, mapAnswersToSave());
  isLoading.value = false;
  
  return true;
};

onMounted(async () => {
  if (needValidate.value) {
    await validate();
    void completeValidation();
  }
});

onBeforeUnmount(async () => {
  await saveIfHasChanges();
});

function mapAnswersToSave() {
  return {
    nutritionInfo: {
      foodObjects: form.restaurants.map((restaurant) => ({
        foodObjectId: restaurant.id,
        foodObjectName: restaurant.name,
        foodObjectType: restaurant.type,
        cuisine: restaurant.cuisine,
        foodObjectOpeningHoursFrom: restaurant.openingHoursFrom,
        foodObjectOpeningHoursTo: restaurant.openingHoursTo,
        attachments: restaurant.attachments,
        photobankUrl: restaurant.photobankUrl,
      })),
      childrenNutrition: form.childrenNutrition,
      nutritionDelivery: form.nutritionDelivery,
    },
  };
}

const deliveryOptions = [
  {
    title: 'Да',
    value: true,
  },
  {
    title: 'Нет',
    value: false,
  },
];
</script>
